import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CBadge,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CFormCheck,
} from "@coreui/react";
import { CChartLine, CChartDoughnut } from "@coreui/react-chartjs";
import CIcon from "@coreui/icons-react";
import { cilOptions } from "@coreui/icons";
import { getTodayAlerts, getPulse, getTopDishesToday, getOrderTypes, getOrderOrigins } from "../../api/reportsApi";
import { SkeletonCard, SkeletonChart } from "../UI/Skeleton/Skeleton";
import { fmtInt, buildPieItems } from "../../utils/chartHelpers";

const VISIBILITY_KEY = "dash:extrasVisibility";
const DEFAULT_VISIBILITY = { alerts: true, pulse: true, topDishes: true, channels: true, sources: true };
const PALETTE = ["#41B883", "#E46651", "#00D8FF", "#DD1B16", "#23e033", "#32aebe"];

function loadVisibility() {
  try {
    const raw = localStorage.getItem(VISIBILITY_KEY);
    return raw ? { ...DEFAULT_VISIBILITY, ...JSON.parse(raw) } : DEFAULT_VISIBILITY;
  } catch {
    return DEFAULT_VISIBILITY;
  }
}

function addDaysStr(dateStr, days) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** п.1 дашборда: алерты дня, почасовой пульс, топ-5 блюд сегодня, каналы продаж за 7 дней. */
export default function DashboardExtrasSection({ serverId }) {
  const [visibility, setVisibility] = useState(loadVisibility);
  const [alerts, setAlerts] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [topDishes, setTopDishes] = useState(null);
  const [channels, setChannels] = useState(null);
  const [sources, setSources] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sourcesLoading, setSourcesLoading] = useState(false);

  const toggle = (key) => {
    setVisibility((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(VISIBILITY_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const load = useCallback(() => {
    if (!serverId) return;
    setLoading(true);
    setAlerts(null);
    setPulse(null);
    setTopDishes(null);
    setChannels(null);
    setSources(null);

    const today = new Date().toISOString().slice(0, 10);
    const weekAgo = addDaysStr(today, -7);
    const yesterday = addDaysStr(today, -1);

    Promise.allSettled([
      getTodayAlerts(serverId),
      getPulse(serverId),
      getTopDishesToday(serverId),
      getOrderTypes(serverId, weekAgo, yesterday),
    ])
      .then(([a, p, d, c]) => {
        if (a.status === "fulfilled") setAlerts(a.value);
        if (p.status === "fulfilled") setPulse(p.value);
        if (d.status === "fulfilled") setTopDishes(d.value);
        if (c.status === "fulfilled") setChannels(c.value);
        setLoading(false);
      })
      .then(() => {
        // Отдельно и после остального — не добавляем ещё один параллельный
        // запрос к iiko в Promise.allSettled выше.
        setSourcesLoading(true);
        return getOrderOrigins(serverId, weekAgo, yesterday)
          .then(setSources)
          .catch(() => setSources(null))
          .finally(() => setSourcesLoading(false));
      });
  }, [serverId]);

  useEffect(() => {
    load();
  }, [load]);

  const channelItems = useMemo(() => channels?.channels || [], [channels]);
  const pie = useMemo(
    () => buildPieItems(channelItems.map((c) => c.name), channelItems.map((c) => c.revenue), PALETTE),
    [channelItems],
  );

  const sourceItems = useMemo(() => sources?.origins || [], [sources]);
  const pieSources = useMemo(
    () => buildPieItems(sourceItems.map((s) => s.name), sourceItems.map((s) => s.revenue), PALETTE),
    [sourceItems],
  );

  const anyVisible =
    visibility.alerts || visibility.pulse || visibility.topDishes || visibility.channels || visibility.sources;
  if (!serverId) return null;

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="mb-0">Сегодня</h6>
        <CDropdown alignment="end">
          <CDropdownToggle color="transparent" caret={false} className="p-1">
            <CIcon icon={cilOptions} />
          </CDropdownToggle>
          <CDropdownMenu className="p-2">
            <div className="small fw-semibold text-body-secondary mb-2 px-2">Показывать виджеты</div>
            <div className="px-2">
              <CFormCheck label="Алерты дня" checked={visibility.alerts} onChange={() => toggle("alerts")} />
              <CFormCheck label="Почасовой пульс" checked={visibility.pulse} onChange={() => toggle("pulse")} />
              <CFormCheck label="Топ-5 блюд сегодня" checked={visibility.topDishes} onChange={() => toggle("topDishes")} />
              <CFormCheck label="Типы заказов" checked={visibility.channels} onChange={() => toggle("channels")} />
              <CFormCheck label="Источник заказа" checked={visibility.sources} onChange={() => toggle("sources")} />
            </div>
          </CDropdownMenu>
        </CDropdown>
      </div>

      {!anyVisible && <div className="text-body-secondary small">Все виджеты скрыты — включите нужные через ⋯.</div>}

      <CRow className="g-3">
        {visibility.alerts && (
          <CCol xs={12} lg={6}>
            <CCard className="h-100">
              <CCardHeader className="fw-semibold">Алерты дня</CCardHeader>
              <CCardBody>
                {loading && !alerts ? (
                  <SkeletonCard lines={2} />
                ) : alerts ? (
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <CBadge color={alerts.dangerous_events_today > 0 ? "danger" : "success"}>
                        Опасных операций: {alerts.dangerous_events_today}
                      </CBadge>
                      {alerts.top_event_type && (
                        <span className="small text-body-secondary">
                          чаще всего: {alerts.top_event_type.label} ({alerts.top_event_type.count})
                        </span>
                      )}
                    </div>
                    {alerts.yesterday_vs_prior_avg_pct != null && (
                      <div className="d-flex align-items-center gap-2">
                        <CBadge color={alerts.is_anomaly ? "warning" : "secondary"}>
                          Вчера: {fmtInt(alerts.yesterday_revenue)} ₽
                        </CBadge>
                        <span className={`small ${alerts.yesterday_vs_prior_avg_pct >= 0 ? "text-success" : "text-danger"}`}>
                          {alerts.yesterday_vs_prior_avg_pct >= 0 ? "↑" : "↓"} {Math.abs(alerts.yesterday_vs_prior_avg_pct)}% к среднему
                          за 30 дней
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-body-secondary small">Нет данных.</div>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        )}

        {visibility.topDishes && (
          <CCol xs={12} lg={6}>
            <CCard className="h-100">
              <CCardHeader className="fw-semibold">Топ-5 блюд сегодня</CCardHeader>
              <CCardBody>
                {loading && !topDishes ? (
                  <SkeletonCard lines={3} />
                ) : topDishes?.items?.length ? (
                  <div className="d-flex flex-column gap-1">
                    {topDishes.items.map((d, i) => (
                      <div key={i} className="d-flex justify-content-between small">
                        <span>{d.name}</span>
                        <span className="text-body-secondary">
                          {fmtInt(d.qty)} шт · {fmtInt(d.revenue)} ₽
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-body-secondary small">Продаж ещё не было.</div>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        )}

        {visibility.pulse && (
          <CCol xs={12} lg={6}>
            <CCard className="h-100">
              <CCardHeader className="fw-semibold">Почасовой пульс: сегодня vs вчера</CCardHeader>
              <CCardBody className="d-flex flex-column">
                {loading && !pulse ? (
                  <SkeletonChart height={340} />
                ) : pulse ? (
                  <div className="flex-grow-1" style={{ minHeight: 340 }}>
                    <CChartLine
                      style={{ height: "100%" }}
                      data={{
                        labels: pulse.today_hours.map((h) => `${h.hour}:00`),
                        datasets: [
                          {
                            label: "Сегодня",
                            borderColor: "#41B883",
                            backgroundColor: "rgba(65,184,131,0.15)",
                            data: pulse.today_hours.map((h) => h.revenue),
                            tension: 0.3,
                          },
                          {
                            label: "Вчера",
                            borderColor: "#8a7a68",
                            borderDash: [4, 4],
                            backgroundColor: "transparent",
                            data: pulse.yesterday_hours.map((h) => h.revenue),
                            tension: 0.3,
                          },
                        ],
                      }}
                      options={{
                        maintainAspectRatio: false,
                        plugins: { legend: { position: "bottom", labels: { font: { size: 13 } } } },
                        scales: { x: { ticks: { font: { size: 12 } } }, y: { ticks: { font: { size: 12 } } } },
                      }}
                    />
                  </div>
                ) : (
                  <div className="text-body-secondary small">Нет данных.</div>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        )}

        {visibility.channels && (
          <CCol xs={12} lg={6}>
            <CCard className="h-100">
              <CCardHeader className="fw-semibold">Типы заказов, 7 дней</CCardHeader>
              <CCardBody>
                {loading && !channels ? (
                  <SkeletonChart height={340} />
                ) : channelItems.length > 0 ? (
                  <div className="d-flex align-items-center gap-4" style={{ minHeight: 340 }}>
                    <div style={{ width: 220, flex: "0 0 auto" }}>
                      <CChartDoughnut
                        data={{
                          labels: pie.map((x) => x.label),
                          datasets: [{ backgroundColor: pie.map((x) => x.color), data: pie.map((x) => x.value) }],
                        }}
                        options={{ plugins: { legend: { display: false } } }}
                      />
                    </div>
                    <div className="d-flex flex-column gap-2 flex-grow-1">
                      {channelItems.map((c, i) => (
                        <div key={c.name} className="d-flex justify-content-between">
                          <span className="d-flex align-items-center gap-2">
                            <span
                              style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: pie[i]?.color }}
                            />
                            {c.name}
                          </span>
                          <span className="text-body-secondary fw-semibold">{c.share_percent}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-body-secondary small">Нет данных.</div>
                )}
                {channels?.note && <div className="small text-body-secondary mt-2">{channels.note}</div>}
              </CCardBody>
            </CCard>
          </CCol>
        )}

        {visibility.sources && (
          <CCol xs={12} lg={6}>
            <CCard className="h-100">
              <CCardHeader className="fw-semibold">Источник заказа, 7 дней</CCardHeader>
              <CCardBody>
                {sourcesLoading && !sources ? (
                  <SkeletonChart height={340} />
                ) : sourceItems.length > 0 ? (
                  <div className="d-flex align-items-center gap-4" style={{ minHeight: 340 }}>
                    <div style={{ width: 220, flex: "0 0 auto" }}>
                      <CChartDoughnut
                        data={{
                          labels: pieSources.map((x) => x.label),
                          datasets: [{ backgroundColor: pieSources.map((x) => x.color), data: pieSources.map((x) => x.value) }],
                        }}
                        options={{ plugins: { legend: { display: false } } }}
                      />
                    </div>
                    <div className="d-flex flex-column gap-2 flex-grow-1">
                      {sourceItems.map((s, i) => (
                        <div key={s.name} className="d-flex justify-content-between">
                          <span className="d-flex align-items-center gap-2">
                            <span
                              style={{
                                display: "inline-block",
                                width: 10,
                                height: 10,
                                borderRadius: 2,
                                background: pieSources[i]?.color,
                              }}
                            />
                            {s.name}
                          </span>
                          <span className="text-body-secondary fw-semibold">
                            {s.commission_percent > 0 ? `${fmtInt(s.net_revenue)} ₽ чистыми` : `${s.share_percent}%`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-body-secondary small">Нет данных.</div>
                )}
                {sources?.note && <div className="small text-body-secondary mt-2">{sources.note}</div>}
              </CCardBody>
            </CCard>
          </CCol>
        )}
      </CRow>
    </div>
  );
}
